import { useState, useEffect, useRef } from 'react'
import { personaAPI, journeyAPI, getWebSocketUrl } from '../../services/api'
import JourneyProgress from './JourneyProgress'

export default function JourneyGenerator({ preselectedPersonaId, onJourneyCompleted }) {
  const [personas, setPersonas] = useState([])
  const [selectedPersonaId, setSelectedPersonaId] = useState(preselectedPersonaId || '')
  const [numEpisodes, setNumEpisodes] = useState(3)
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)
  const wsRef = useRef(null)

  useEffect(() => {
    personaAPI.list()
      .then(setPersonas)
      .catch(() => setError('Failed to load personas'))
  }, [])

  useEffect(() => {
    if (preselectedPersonaId) {
      setSelectedPersonaId(preselectedPersonaId)
    }
  }, [preselectedPersonaId])

  const handleGenerate = async () => {
    if (!selectedPersonaId) return
    setGenerating(true)
    setError(null)
    setProgress({
      status: 'connecting',
      currentEpisode: 0,
      totalEpisodes: numEpisodes,
      startTime: Date.now()
    })

    try {
      const { journey_id, ws_url } = await journeyAPI.generate(selectedPersonaId, numEpisodes)

      // Connect WebSocket with token
      const token = localStorage.getItem('sessionToken') || localStorage.getItem('authToken')
      const wsUrl = `${ws_url}?token=${encodeURIComponent(token)}`

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setProgress(prev => ({ ...prev, status: 'started', journeyId: journey_id }))
      }

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data)
        const { type, data } = msg

        switch (type) {
          case 'journey_started':
            setProgress(prev => ({
              ...prev,
              status: 'generating',
              patientName: data.patient_name
            }))
            break

          case 'episode_started':
            setProgress(prev => ({
              ...prev,
              currentEpisode: data.episode_number,
              currentTitle: data.title,
              currentPhase: data.phase
            }))
            break

          case 'episode_completed':
            setProgress(prev => ({
              ...prev,
              completedEpisodes: (prev.completedEpisodes || 0) + 1,
              lastCompletedTitle: data.title
            }))
            break

          case 'journey_completed':
            setProgress(prev => ({
              ...prev,
              status: 'completed',
              journeyId: data.journey_id
            }))
            setGenerating(false)
            break

          case 'journey_failed':
            setError(data.error || 'Journey generation failed')
            setProgress(prev => ({ ...prev, status: 'failed' }))
            setGenerating(false)
            break
        }
      }

      ws.onerror = () => {
        setError('WebSocket connection error')
        setGenerating(false)
      }

      ws.onclose = () => {
        wsRef.current = null
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start journey generation')
      setGenerating(false)
      setProgress(null)
    }
  }

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  const canGenerate = selectedPersonaId && !generating

  return (
    <div className="space-y-6">
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-xl mb-4">Generate Patient Journey</h2>
          <p className="text-base-content/70 mb-6">
            Select a persona and choose the number of episodes to generate a complete
            clinical journey with AI-powered multi-agent dialogue.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Persona dropdown */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Patient Persona</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedPersonaId}
                onChange={(e) => setSelectedPersonaId(e.target.value)}
                disabled={generating}
              >
                <option value="">Select persona...</option>
                {personas.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {personas.length === 0 && (
                <label className="label">
                  <span className="label-text-alt text-warning">
                    No personas yet. Create one first in the "Create Persona" tab.
                  </span>
                </label>
              )}
            </div>

            {/* Episode count */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Number of Episodes</span>
              </label>
              <input
                type="range"
                min="1"
                max="24"
                value={numEpisodes}
                onChange={(e) => setNumEpisodes(parseInt(e.target.value))}
                className="range range-primary"
                disabled={generating}
              />
              <div className="flex justify-between text-xs px-1 mt-1">
                {[1, 4, 8, 12, 16, 20, 24].map((n) => (
                  <span key={n} className={numEpisodes === n ? 'font-bold text-primary' : 'text-base-content/50'}>
                    {n}
                  </span>
                ))}
              </div>
              <label className="label">
                <span className="label-text-alt">
                  {numEpisodes} episode{numEpisodes !== 1 ? 's' : ''} (~{numEpisodes * 30}s generation time)
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div className="alert alert-error mt-4">
              <span>{error}</span>
            </div>
          )}

          <div className="card-actions justify-end mt-4">
            <button
              className={`btn btn-primary ${generating ? 'btn-disabled' : ''}`}
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              {generating ? 'Generating...' : 'Generate Journey'}
            </button>
          </div>
        </div>
      </div>

      {progress && (
        <JourneyProgress
          progress={progress}
          onViewJourney={() => onJourneyCompleted?.()}
        />
      )}
    </div>
  )
}
